import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { CloseDialog, OpenDialog, SendBookClubHistory } from './bookclub-links.actions';
import { patch } from '@ngxs/store/operators';
import { ExperienceModel } from 'src/app/models/experience-link';
import { BookclubLinksDialogFactory } from './services/bookclub-links.factory';
import { BookclubHistoryApiService } from 'src/app/services/api/bookclub-history-api.service';
import { UserService } from 'src/app/services/user.service';
import { finalize, tap } from 'rxjs';
import { createWindowDeferred } from '../../../utils/create-window-deferred';
import { Record } from 'src/app/models/record';
import { AnalyticsService } from '../../../analytics/analytics.service';
import { ExperienceType } from 'src/app/models/experiences';

export type BookclubLinksStateModel = {
  record: Record | null;
  links: ExperienceModel[];
  resourceId: string;
  open: boolean;
};

export const initialState: BookclubLinksStateModel = {
  record: null,
  links: [],
  resourceId: '',
  open: false,
};

@State<BookclubLinksStateModel>({
  name: 'bookclubLinks',
  defaults: initialState,
})
@Injectable()
export class BookclubLinksState {
  private readonly userService = inject(UserService);
  private readonly bookclubLinksDialogFactory = inject(BookclubLinksDialogFactory);
  private readonly bookClubHistoryService = inject(BookclubHistoryApiService);
  private readonly analyticsService = inject(AnalyticsService);

  @Selector()
  static open(state: BookclubLinksStateModel): boolean {
    return state.open;
  }

  @Selector()
  static title(state: BookclubLinksStateModel): string {
    return state.record?.title || '';
  }

  @Selector()
  static links(state: BookclubLinksStateModel): ExperienceModel[] {
    return state.links.filter((link) => link.experienceLinks.length);
  }

  @Selector()
  static resourceId(state: BookclubLinksStateModel): string {
    return state.resourceId;
  }

  @Action(OpenDialog)
  openDialog(context: StateContext<BookclubLinksStateModel>, { record, links, resourceId }: OpenDialog) {
    context.setState(
      patch({
        open: true,
        record: record,
        links: this.bookclubLinksDialogFactory.buildLinks(links),
        resourceId,
      })
    );
  }

  @Action(CloseDialog)
  closeDialog(context: StateContext<BookclubLinksStateModel>) {
    context.setState(initialState);
  }

  @Action(SendBookClubHistory)
  sendBookClubHistory(
    context: StateContext<BookclubLinksStateModel>,
    { recordId, link, dontShow }: SendBookClubHistory
  ) {
    this.analyticsService.sendContentViewedEvent(
      context.getState().record!,
      context.getState().record?.freeDownload ? 'free_access' : 'checkout',
      'streaming',
      { learningExperienceId: link.type !== ExperienceType.PLAIN ? link.learningExperienceId : undefined }
    );

    const [openWindow] = createWindowDeferred();

    return this.bookClubHistoryService
      .saveBookClubHistory(this.userService.getUserId(), recordId, link.learningExperienceId, dontShow)
      .pipe(
        tap(() => {
          openWindow(link.url);
        }),
        finalize(() => {
          context.setState(initialState);
        })
      );
  }
}
